/**
 * InputManager — unified pointer input: tap-to-pick, drag-to-orbit, pinch-zoom.
 * 拾取优先级：弹珠 → 落点环 → 孔位吸附（半径 0.5 世界单位）→ 空处。
 * 100ms 反馈底线：hover 高亮与 tap 派发均在事件帧内完成。
 */
import * as THREE from 'three';
import type { HexPos } from '@shared/types/game';
import { parseHexKey } from '@shared/utils/hexMath';
import type { CameraRig } from './CameraRig';

export interface InputHandlers {
  /** Marble tapped (userData.key). */
  onPickMarble?: (key: string) => void;
  /** Marker or hole snapped (legal-target judgement belongs to the scene). */
  onPickHole?: (pos: HexPos) => void;
  onPickEmpty?: () => void;
  onHoverMarble?: (key: string | null) => void;
  /**
   * Live set of the currently selectable destination holes (scene-owned).
   * When a snapped hole is in this set it wins the pick outright — even if a
   * foreground marble sits on the ray (happens at low orbit angles, where the
   * marble you jump over occludes its landing hole). Without this priority a
   * legal target click can be swallowed by a marble re-select.
   */
  targetKeys?: () => ReadonlySet<string> | null;
}

export interface InputManagerOptions {
  dom: HTMLElement;
  camera: THREE.PerspectiveCamera;
  rig: CameraRig;
  /** Registered pickables (marbles + markers). */
  pickables: () => THREE.Object3D[];
  /**
   * Accessors — NOT snapshots. Scenes rebuild the board and swap the hole map
   * / felt height via GameEngine.registerHoles(); reading through closures
   * keeps every scene's registry live, whereas capturing the values here
   * freezes the initial (empty) map forever and kills all hole picking.
   */
  holeWorld: () => Map<string, THREE.Vector3>;
  feltY: () => number;
  handlers: InputHandlers;
}

interface PointerRecord {
  x: number;
  y: number;
  startX: number;
  startY: number;
  downTime: number;
  moved: boolean;
}

type PickResult = { kind: 'marble'; key: string } | { kind: 'hole'; pos: HexPos } | null;

export class InputManager {
  private readonly o: InputManagerOptions;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointers = new Map<number, PointerRecord>();
  private pinchDist = 0;
  private hoverKey: string | null = null;
  private readonly planeNormal = new THREE.Vector3(0, 1, 0);
  private readonly plane: THREE.Plane;
  private attached = false;

  constructor(options: InputManagerOptions) {
    this.o = options;
    this.plane = new THREE.Plane(this.planeNormal, -options.feltY());
  }

  /** Rebind interaction handlers (scenes swap these on enter). */
  setHandlers(handlers: InputHandlers): void {
    this.o.handlers = handlers;
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const d = this.o.dom;
    d.style.touchAction = 'none';
    d.addEventListener('pointerdown', this.onDown);
    d.addEventListener('pointermove', this.onMove);
    d.addEventListener('pointerup', this.onUp);
    d.addEventListener('pointercancel', this.onUp);
    d.addEventListener('pointerleave', this.onLeave);
    d.addEventListener('wheel', this.onWheel, { passive: false });
  }

  dispose(): void {
    if (!this.attached) return;
    this.attached = false;
    const d = this.o.dom;
    d.removeEventListener('pointerdown', this.onDown);
    d.removeEventListener('pointermove', this.onMove);
    d.removeEventListener('pointerup', this.onUp);
    d.removeEventListener('pointercancel', this.onUp);
    d.removeEventListener('pointerleave', this.onLeave);
    d.removeEventListener('wheel', this.onWheel);
    this.pointers.clear();
    this.setHover(null);
  }

  private onDown = (e: PointerEvent): void => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    this.pointers.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      downTime: performance.now(),
      moved: false,
    });
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      if (a && b) this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };

  private onMove = (e: PointerEvent): void => {
    const rec = this.pointers.get(e.pointerId);
    if (!rec) {
      this.hoverUpdate(e);
      return;
    }
    const dx = e.clientX - rec.x;
    const dy = e.clientY - rec.y;
    rec.x = e.clientX;
    rec.y = e.clientY;

    if (this.pointers.size >= 2) {
      // Pinch zoom — orbit suppressed during pinch.
      const [a, b] = [...this.pointers.values()];
      if (a && b) {
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.pinchDist > 0 && dist > 0) this.o.rig.zoomBy(this.pinchDist / dist);
        this.pinchDist = dist;
      }
      return;
    }

    const total = Math.hypot(e.clientX - rec.startX, e.clientY - rec.startY);
    if (total > 6) rec.moved = true;
    if (rec.moved) {
      this.o.rig.orbit(dx, dy);
      this.setHover(null);
    }
  };

  private onUp = (e: PointerEvent): void => {
    const rec = this.pointers.get(e.pointerId);
    if (!rec) return;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinchDist = 0;
    if (rec.moved) return;
    if (performance.now() - rec.downTime > 600) return;

    const pick = this.pickAt(e.clientX, e.clientY);
    if (!pick) {
      this.o.handlers.onPickEmpty?.();
      return;
    }
    if (pick.kind === 'marble') this.o.handlers.onPickMarble?.(pick.key);
    else this.o.handlers.onPickHole?.(pick.pos);
  };

  private onLeave = (): void => {
    this.setHover(null);
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.o.rig.zoomBy(Math.exp(e.deltaY * 0.0011));
  };

  private hoverUpdate(e: PointerEvent): void {
    const pick = this.pickAt(e.clientX, e.clientY);
    this.setHover(pick?.kind === 'marble' ? pick.key : null);
  }

  private setHover(key: string | null): void {
    if (this.hoverKey === key) return;
    this.hoverKey = key;
    this.o.handlers.onHoverMarble?.(key);
  }

  /** Nearest hole to the felt-plane intersection of the pick ray (radius 0.5). */
  private snapHole(): { key: string; pos: HexPos } | null {
    // The felt height is scene-owned and can change on board rebuilds.
    this.plane.constant = -this.o.feltY();
    const pt = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.plane, pt)) return null;
    let best: { key: string; pos: HexPos; d2: number } | null = null;
    for (const [k, v] of this.o.holeWorld()) {
      const d2 = v.distanceToSquared(pt);
      if (!best || d2 < best.d2) best = { key: k, pos: parseHexKey(k), d2 };
    }
    return best && best.d2 < 0.25 ? best : null;
  }

  private pickAt(cx: number, cy: number): PickResult {
    const rect = this.o.dom.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const ndc = new THREE.Vector2(
      ((cx - rect.left) / rect.width) * 2 - 1,
      -((cy - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, this.o.camera);

    // A legal destination always wins the pick, even through a foreground
    // marble (low-orbit occlusion). Selection stays marble-first otherwise.
    const snapped = this.snapHole();
    if (snapped && this.o.handlers.targetKeys?.()?.has(snapped.key)) {
      return { kind: 'hole', pos: snapped.pos };
    }

    for (const hit of this.raycaster.intersectObjects(this.o.pickables(), false)) {
      const ud = hit.object.userData as { kind?: string; key?: string; pos?: HexPos };
      if (ud.kind === 'marble' && ud.key) return { kind: 'marble', key: ud.key };
      if (ud.kind === 'marker' && ud.pos) return { kind: 'hole', pos: ud.pos };
    }

    if (snapped) return { kind: 'hole', pos: snapped.pos };
    return null;
  }
}
